import { motion } from "framer-motion";
import { RichText } from "./RichText";

export type MascotMood = "happy" | "thinking" | "cheer" | "oops" | "wave" | "proud";

const FACE: Record<MascotMood, string> = {
  happy: "🦉",
  thinking: "🦉",
  cheer: "🦉",
  oops: "🦉",
  wave: "🦉",
  proud: "🦉",
};

const BADGE: Record<MascotMood, string | null> = {
  happy: null,
  thinking: "🤔",
  cheer: "🎉",
  oops: "💡",
  wave: "👋",
  proud: "⭐",
};

const ANIM: Record<MascotMood, any> = {
  happy: { y: [0, -4, 0], transition: { repeat: Infinity, duration: 2.4, ease: "easeInOut" } },
  thinking: { rotate: [0, -6, 6, 0], transition: { repeat: Infinity, duration: 3, ease: "easeInOut" } },
  cheer: { y: [0, -14, 0], scale: [1, 1.12, 1], transition: { repeat: 2, duration: 0.5 } },
  oops: { x: [0, -5, 5, -3, 3, 0], transition: { duration: 0.5 } },
  wave: { rotate: [0, 12, -8, 12, 0], transition: { duration: 1.2 } },
  proud: { scale: [1, 1.08, 1], transition: { repeat: Infinity, duration: 1.8 } },
};

interface MascotProps {
  mood?: MascotMood;
  message?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

/** Owlbert, the EduFocal Academy study owl. Emoji-based so there is no art pipeline. */
export function Mascot({ mood = "happy", message, size = "md", className = "" }: MascotProps) {
  const dim = size === "lg" ? "w-20 h-20 text-5xl" : size === "sm" ? "w-10 h-10 text-2xl" : "w-14 h-14 text-3xl";
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <div className="relative flex-shrink-0">
        <motion.div
          animate={ANIM[mood]}
          className={`${dim} rounded-full bg-gradient-to-br from-amber-200 to-orange-300 dark:from-amber-300 dark:to-orange-400 flex items-center justify-center shadow-md select-none`}
          aria-label="Owlbert the owl"
        >
          {FACE[mood]}
        </motion.div>
        {BADGE[mood] && (
          <span className="absolute -top-1 -right-1 text-lg drop-shadow animate-in zoom-in-50 duration-300">
            {BADGE[mood]}
          </span>
        )}
      </div>
      {message && (
        <div className="relative flex-1 rounded-2xl rounded-tl-sm bg-white dark:bg-slate-800 border border-border shadow-sm px-4 py-3 text-base animate-in fade-in slide-in-from-bottom-1 duration-300">
          <RichText text={message} />
        </div>
      )}
    </div>
  );
}
